import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, UserMinus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ResellerFormDialog } from "@/pages/admin/ResellerFormDialog";
import { api, brl, formatError } from "@/lib/api";

const KINDS = {
  resellers: {
    tid: "reseller", title: "Revendedores", col: "Revendedor", newLabel: "Novo revendedor", editTitle: "Editar revendedor",
    desc: "Revendedores veem e pagam o preço de revenda de cada produto e acompanham seus pedidos.",
    empty: "Nenhum revendedor cadastrado.", created: "Revendedor cadastrado", updated: "Revendedor atualizado",
    removeTitle: "Remover perfil de revendedor?", removeDesc: "passa a comprar com preço normal.", removed: "Perfil de revendedor removido",
    convertHint: "Se o e-mail já tiver conta de cliente, ela vira conta de revendedor e mantém os pedidos.",
  },
  stores: {
    tid: "store", title: "Lojas", col: "Loja", newLabel: "Nova loja", editTitle: "Editar loja",
    desc: "Lojas fazem pedidos para os clientes do balcão, com preço normal, e podem receber em Pix, cartão ou dinheiro.",
    empty: "Nenhuma loja cadastrada.", created: "Loja cadastrada", updated: "Loja atualizada",
    removeTitle: "Remover perfil de loja?", removeDesc: "deixa de ter a opção de pagamento em dinheiro.", removed: "Perfil de loja removido",
    convertHint: "Se o e-mail já tiver conta de cliente, ela vira conta de loja e mantém os pedidos.",
  },
};

export default function AdminResellers({ kind = "resellers" }) {
  const cfg = KINDS[kind];
  const { tid } = cfg;
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = () => api.get(`/admin/${kind}`).then((r) => setList(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmRemove = async () => {
    try {
      await api.delete(`/admin/${kind}/${removing.user_id}`);
      toast.success(cfg.removed);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setRemoving(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid={`${tid}s-title`}>{cfg.title}</h1>
          <p className="text-slate-500">{cfg.desc}</p>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-full bg-[#0099DD] hover:bg-sky-700" data-testid={`btn-new-${tid}`}><Plus className="h-4 w-4" /> {cfg.newLabel}</Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <Table data-testid={`admin-${tid}s-table`}>
          <TableHeader>
            <TableRow><TableHead>{cfg.col}</TableHead><TableHead>Contato</TableHead><TableHead>CPF / CNPJ</TableHead><TableHead>Pedidos</TableHead><TableHead>Total pago</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.user_id} data-testid={`${tid}-row-${r.user_id}`}>
                <TableCell className="font-semibold">{r.name}</TableCell>
                <TableCell><p className="text-sm">{r.email}</p><p className="text-xs text-slate-500">{r.phone}</p></TableCell>
                <TableCell className="text-sm text-slate-600">{r.document || "—"}</TableCell>
                <TableCell className="font-mono">{r.orders_count}</TableCell>
                <TableCell className="font-mono">{brl(r.paid_total)}</TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="icon" onClick={() => setEditing(r)} aria-label="Editar" data-testid={`btn-edit-${tid}-${r.user_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setRemoving(r)} aria-label="Remover perfil" data-testid={`btn-remove-${tid}-${r.user_id}`}><UserMinus className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <p className="p-10 text-center text-sm text-slate-500" data-testid={`no-${tid}s`}>{cfg.empty}</p>}
      </div>
      {editing && <ResellerFormDialog kind={kind} cfg={cfg} reseller={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cfg.removeTitle}</AlertDialogTitle>
            <AlertDialogDescription>{removing?.name} {cfg.removeDesc} A conta e os pedidos continuam salvos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`btn-cancel-remove-${tid}`}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-600 hover:bg-red-700" data-testid={`btn-confirm-remove-${tid}`}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
